import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, MoreHorizontal, Pencil, XCircle, Route, ArrowRight, AlertCircle } from "lucide-react";
import { useVerticalOptions } from "@/hooks/useVocabulary";

export default function CalendarRoutingManager({ tenantId = null }) {
  const [routes, setRoutes] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  
  // P1 VOCABULARY: Use vertical options for dropdown
  const { options: verticalOptions } = useVerticalOptions();
  
  // Form state
  const [calendarId, setCalendarId] = useState("");
  const [leadSourceId, setLeadSourceId] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [playbookKey, setPlaybookKey] = useState("");
  const [verticalKey, setVerticalKey] = useState("");  // P1: Vertical attribution
  
  const isMultiWorkspace = workspaces.filter(ws => ws.status !== "inactive").length > 1;

  // P0 TENANT ISOLATION: Build params with tenant_id for SuperAdmin cross-tenant access
  const buildTenantParams = useCallback(() => {
    return tenantId ? { tenant_id: tenantId } : {};
  }, [tenantId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildTenantParams();
      const [routesRes, leadSourcesRes, workspacesRes] = await Promise.all([
        api.get("/admin/calendar-routes", { params }),
        api.get("/admin/lead-sources", { params }),
        api.get("/tenants/workspaces/all", { params }),
      ]);
      setRoutes(routesRes.data?.routes || []);
      setLeadSources(leadSourcesRes.data?.lead_sources || leadSourcesRes.data || []);
      setWorkspaces(workspacesRes.data || []);
    } catch (e) {
      toast({
        title: "Error loading data",
        description: e?.response?.data?.detail || "Failed to load calendar routes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [buildTenantParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setCalendarId("");
    setLeadSourceId("");
    setWorkspaceId("");
    setPlaybookKey("");
    setVerticalKey("");
    setEditingRoute(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (route) => {
    setEditingRoute(route);
    setCalendarId(route.calendar_id || "");
    setLeadSourceId(route.lead_source_id || "");
    setWorkspaceId(route.workspace_id || "");
    setPlaybookKey(route.default_playbook_key || "");
    setVerticalKey(route.vertical_key || "");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!calendarId.trim()) {
      toast({ title: "Calendar ID required", variant: "destructive" });
      return;
    }
    if (!leadSourceId) {
      toast({ title: "Lead Source required", variant: "destructive" });
      return;
    }
    if (!workspaceId) {
      toast({ title: "Workspace required", variant: "destructive" });
      return;
    }
    if (!verticalKey) {
      toast({ title: "Vertical required", variant: "destructive" });
      return;
    }

    try {
      const payload = {
        calendar_id: calendarId.trim(),
        lead_source_id: leadSourceId,
        workspace_id: workspaceId,
        default_playbook_key: playbookKey || null,
        vertical_key: verticalKey,
      };

      if (editingRoute) {
        await api.put(`/admin/calendar-routes/${editingRoute.id}`, payload);
        toast({ title: "Route updated" });
      } else {
        await api.post("/admin/calendar-routes", payload);
        toast({ title: "Route created" });
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (e) {
      toast({
        title: "Save failed",
        description: e?.response?.data?.detail || "Could not save route",
        variant: "destructive",
      });
    }
  };

  const handleToggleStatus = async (route) => {
    const newStatus = route.status === "active" ? "inactive" : "active";
    try {
      await api.put(`/admin/calendar-routes/${route.id}`, { status: newStatus });
      toast({ title: `Route ${newStatus === "active" ? "activated" : "deactivated"}` });
      fetchData();
    } catch (e) {
      toast({
        title: "Status change failed",
        description: e?.response?.data?.detail || "Could not update status",
        variant: "destructive",
      });
    }
  };

  const getLeadSourceName = (id) => {
    const ls = leadSources.find((s) => s.id === id);
    return ls?.name || id;
  };

  const getWorkspaceName = (id) => {
    if (!id) return "—";
    const ws = workspaces.find((w) => w.id === id);
    return ws?.name || id;
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return "—";
    return new Date(isoStr).toLocaleDateString();
  };

  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <div className="space-y-1.5">
          <CardTitle className="text-slate-100 flex items-center gap-2">
            <Route className="h-5 w-5" /> Calendar Routing Rules
          </CardTitle>
          <CardDescription className="text-slate-400">
            Map GHL calendars to lead sources and workspaces for deterministic classification
          </CardDescription>
        </div>
        <Button
          onClick={openCreateDialog}
          data-testid="create-calendar-route-btn"
        >
          <Plus className="mr-2 h-4 w-4" /> New Route
        </Button>
      </CardHeader>
      <CardContent>
        {isMultiWorkspace && (
          <div className="mb-4 flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 p-3">
            <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-200">
              <strong>Multi-Workspace Account:</strong> Routes without an explicit workspace will fall back to "Unassigned". 
              Set a workspace on each route for proper lead segmentation.
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-slate-400 py-8 text-center">Loading routes...</div>
        ) : routes.length === 0 ? (
          <div className="text-slate-400 py-8 text-center">
            No calendar routing rules configured. Click "New Route" to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="calendar-routes-table">
              <thead className="border-b border-white/10 text-slate-300">
                <tr>
                  <th className="py-3 px-2 text-left">Calendar ID</th>
                  <th className="py-3 px-2 text-left">Lead Source</th>
                  <th className="py-3 px-2 text-left">Channel</th>
                  <th className="py-3 px-2 text-left">Workspace</th>
                  <th className="py-3 px-2 text-left">Vertical</th>
                  <th className="py-3 px-2 text-left">Status</th>
                  <th className="py-3 px-2 text-left">Updated</th>
                  <th className="py-3 px-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {routes.map((route) => (
                  <tr
                    key={route.id}
                    className="border-b border-white/5"
                    data-testid={`calendar-route-row-${route.id}`}
                  >
                    <td className="py-3 px-2 text-slate-100 font-mono text-xs">
                      {route.calendar_id}
                    </td>
                    <td className="py-3 px-2 text-slate-200">
                      {route.lead_source_name || getLeadSourceName(route.lead_source_id)}
                    </td>
                    <td className="py-3 px-2">
                      <Badge variant="outline" className="text-xs border-indigo-400/50 text-indigo-300">
                        {route.lead_source_channel || "—"}
                      </Badge>
                    </td>
                    <td className="py-3 px-2 text-slate-300">
                      {route.workspace_name || getWorkspaceName(route.workspace_id)}
                      {!route.workspace_id && isMultiWorkspace && (
                        <span className="ml-1 text-amber-400 text-xs">(→ Unassigned)</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-slate-300">
                      {(() => {
                        const vertKey = route.effective_vertical_key || route.vertical_key;
                        if (!vertKey) return "—";
                        const vertLabel = verticalOptions.find(v => v.value === vertKey)?.label || vertKey;
                        return (
                          <>
                            {vertLabel}
                            {route.effective_vertical_key && !route.vertical_key && (
                              <span className="text-slate-500 ml-1 text-xs">(from source)</span>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-2">
                      <Badge
                        variant={route.status === "active" ? "default" : "secondary"}
                        className={
                          route.status === "active"
                            ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
                            : "bg-slate-500/20 text-slate-400 border-slate-500/30"
                        }
                      >
                        {route.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-2 text-slate-400 text-xs">
                      {formatDate(route.updated_at)}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-400 hover:text-slate-200 hover:bg-white/5"
                            data-testid={`calendar-route-actions-${route.id}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(route)}>
                            <Pencil className="mr-2 h-4 w-4" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleStatus(route)}>
                            {route.status === "active" ? (
                              <>
                                <XCircle className="mr-2 h-4 w-4" /> Deactivate
                              </>
                            ) : (
                              <>
                                <ArrowRight className="mr-2 h-4 w-4" /> Activate
                              </>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
          <DialogContent className="bg-slate-900 border-white/10 text-slate-100">
            <DialogHeader>
              <DialogTitle>
                {editingRoute ? "Edit Calendar Route" : "Create Calendar Route"}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Map a GHL calendar to a lead source for deterministic lead classification.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-slate-200">Calendar ID *</Label>
                <Input
                  value={calendarId}
                  onChange={(e) => setCalendarId(e.target.value)}
                  placeholder="e.g., abc123xyz (from GHL)"
                  className="bg-white/5 border-white/10 text-slate-100"
                  disabled={!!editingRoute}
                  data-testid="calendar-route-calendar-id"
                />
                {editingRoute && (
                  <p className="text-xs text-slate-500">
                    Calendar ID cannot be changed. Create a new route instead.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-slate-200">Lead Source *</Label>
                <Select value={leadSourceId} onValueChange={setLeadSourceId}>
                  <SelectTrigger
                    className="bg-white/5 border-white/10 text-slate-100"
                    data-testid="calendar-route-lead-source"
                  >
                    <SelectValue placeholder="Select lead source" />
                  </SelectTrigger>
                  <SelectContent>
                    {leadSources.filter((ls) => ls.status !== "inactive").map((ls) => (
                      <SelectItem key={ls.id} value={ls.id}>
                        {ls.name} ({ls.channel})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Leads from this calendar will be classified with this lead source.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-200">
                  Workspace *
                </Label>
                <Select 
                  value={workspaceId || "__none__"} 
                  onValueChange={(val) => setWorkspaceId(val === "__none__" ? "" : val)}
                >
                  <SelectTrigger
                    className="bg-white/5 border-white/10 text-slate-100"
                    data-testid="calendar-route-workspace"
                  >
                    <SelectValue placeholder="Select workspace" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" disabled className="text-slate-500">Select a workspace</SelectItem>
                    {workspaces.filter((ws) => ws.status !== "inactive").map((ws) => (
                      <SelectItem key={ws.id} value={ws.id}>
                        {ws.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isMultiWorkspace && !workspaceId && (
                  <p className="text-xs text-amber-400">
                    Multi-workspace account: Please select a workspace for proper lead segmentation.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-slate-200">Playbook Override (Optional)</Label>
                <Input
                  value={playbookKey}
                  onChange={(e) => setPlaybookKey(e.target.value)}
                  placeholder="e.g., referral_playbook"
                  className="bg-white/5 border-white/10 text-slate-100"
                  data-testid="calendar-route-playbook"
                />
                <p className="text-xs text-slate-500">
                  Override the lead source's default playbook for leads from this calendar.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-200">Vertical *</Label>
                <Select
                  value={verticalKey || "none"}
                  onValueChange={(v) => setVerticalKey(v === "none" ? "" : v)}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-slate-100" data-testid="calendar-route-vertical">
                    <SelectValue placeholder="Select vertical..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="none" disabled className="text-slate-500">
                      Select a vertical
                    </SelectItem>
                    {verticalOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-slate-100">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Required: Defines the marketing vertical for leads from this calendar.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                data-testid="calendar-route-save-btn"
              >
                {editingRoute ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
